/** @jsx jsx */
import { useMemo } from 'react';
import { jsx } from '@compiled/react';

import Tooltip from '@atlaskit/tooltip';
import { CommentEdited } from '@atlaskit/comment';
import { Inline } from '@atlaskit/primitives';
import Timestamp from '@atlassian/jira-issue-timestamp/src/main.tsx';
import { useIntl } from '@atlassian/jira-intl';
import draftMessages from '@atlassian/jira-issue-view-common/src/messages/drafts.tsx';
import messages from '../messages.tsx';
import { useCommentContext } from './CommentContextProvider.tsx';
import CommentSubText from './CommentSubtext.tsx';

const CommentEditedIndicator = () => {
	const {
		isDeleted,
		displayNameForUpdateAuthor,
		hasFloatingMenu,
		updatedDate,
		hasDraft,
		edited,
		isSaving,
	} = useCommentContext();
	const { formatMessage } = useIntl();

	const tooltipContent = useMemo(() => {
		return formatMessage(messages.editedBy, {
			author: displayNameForUpdateAuthor,
			timestamp: <Timestamp value={updatedDate || ''} />,
		});
	}, [displayNameForUpdateAuthor, formatMessage, updatedDate]);

	const shouldDisplayDraft = hasFloatingMenu && hasDraft;

	const editedMessage = useMemo(() => {
		const chunks = [];
		const editedText = formatMessage(messages.edited).toLocaleLowerCase();
		const draftText = formatMessage(draftMessages.draftIndicatorAfterEdited);

		if (edited) {
			chunks.push(editedText);
		}
		if (shouldDisplayDraft) {
			chunks.push(draftText);
		}
		if (chunks.length === 0) {
			return null;
		}
		return chunks.join(' • ');
	}, [edited, formatMessage, shouldDisplayDraft]);

	if (isDeleted || isSaving || (!edited && !shouldDisplayDraft)) {
		return null;
	}
	return (
		<Tooltip
			content={tooltipContent}
			position="top"
			// Test ID will be changed once the old comment component is deleted
			// eslint-disable-next-line jira/integration/test-id-by-folder-structure
			testId="issue-comment-base.ui.comment.ak-tool-tip"
		>
			<Inline alignBlock="center">
				<CommentSubText>
					<CommentEdited>{`(${editedMessage})`}</CommentEdited>
				</CommentSubText>
			</Inline>
		</Tooltip>
	);
};
export default CommentEditedIndicator;
